import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Container,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import Cookies from "js-cookie";
import UserForm from "./UserForm";
import AutohideSnackbar from "../LayoutModule/autohideSnackbar";
import AppGlobalStaticData from "../../appdata/appStaticData";
import PageContentLayout from "../LayoutModule/pageContentLayout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const UserList = () => {
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [snackbarProperties, setSnackbarProperties] = useState({
    duration: 0,
    message: "",
    severity: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const globalData = new AppGlobalStaticData();

  useEffect(() => {
    fetchUserList();
  }, []);

  useEffect(() => {
    if (snackbarProperties.message) {
      setSnackbarOpen(true);
    }
  }, [snackbarProperties.message]);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchUserList = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("auth_token");
      const response = await axios.get(`${API_BASE_URL}/api/v1/user/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.status === "Success") {
        const userListWithSNO = response.data.data.map((user, index) => ({
          sno: index + 1,
          ...user,
        }));
        setSnackbarProperties({
          duration: globalData.Duration,
          message: globalData.UserListSuccess,
          severity: globalData.SeveritySuccess,
        });
        setUserList(userListWithSNO);
      } else {
        setSnackbarProperties({
          duration: globalData.Duration,
          message: globalData.UserListFailed,
          severity: globalData.SeverityError,
        });
      }
    } catch (error) {
      setSnackbarProperties({
        duration: globalData.Duration,
        message: globalData.UserListFailed,
        severity: globalData.SeverityError,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (id) => {
    setSelectedUserId(id);
    setOpen(true);
  };

  const handleDeleteConfirmation = (id) => {
    setSelectedUserId(id);
    setConfirmDeleteOpen(true);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const token = Cookies.get("auth_token");
      const response = await axios.delete(
        `${API_BASE_URL}/api/v1/user/${selectedUserId}/delete`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status === "Success") {
        setSnackbarProperties({
          duration: globalData.Duration,
          message: globalData.DeleteUserSuccess,
          severity: globalData.SeveritySuccess,
        });
        fetchUserList(); // Re-fetch user list after deletion
      } else {
        setSnackbarProperties({
          duration: globalData.Duration,
          message: globalData.DeleteUserFailed,
          severity: globalData.SeverityError,
        });
      }
    } catch (error) {
      setSnackbarProperties({
        duration: globalData.Duration,
        message: globalData.DeleteUserFailed,
        severity: globalData.SeverityError,
      });
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedUserId(null);
  };

  const columns = [
    { field: "sno", headerName: "S.NO", width: 90, sortable: false },
    { field: "name", headerName: "Name", width: 180, sortable: true },
    { field: "email", headerName: "Email", width: 180, sortable: true },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (param) => (
        <Box>
          <IconButton onClick={() => handleEdit(param.id)} color="primary">
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteConfirmation(param.id)}
            color="secondary"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return <CircularProgress />;
  }

  return (

    <PageContentLayout pageName={"User List"}  tooltip ="Add User" addcallbackFunction={() => setOpen(true)}>
      <div style={{  width: "auto", margin: "auto" }}>
        <DataGrid rows={userList} columns={columns} pageSize={5} />
      </div>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUserId ? "Edit User" : "Create User"}
        </DialogTitle>
        <DialogContent>
          <UserForm id={selectedUserId} onClose={handleClose} />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this user? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AutohideSnackbar
        open={snackbarOpen}
        duration={snackbarProperties.duration}
        message={snackbarProperties.message}
        severity={snackbarProperties.severity}
        handleClose={handleSnackbarClose}
      />
    </PageContentLayout>
  );
};

export default UserList;
